import cx from 'classnames';
import { keyBy } from 'lodash/fp';
import { VIEW, E_CW_STATUS } from '../../interfaces';
import { initRootStore } from '../../RootStore';
import {
    IActiveDimension,
    IExportChartConfig,
} from '../../services/lib/APIServiceInterfaces';
import rootStore from '../../RootStore';
import moment from 'moment';
import { IHeatmapStore } from 'stores/HeatmapStore';
import ColorStack from '../../utils/ColorStack';
import GQLoader from 'components/GQLoader/GQLoader';
import { useEffect, useState } from 'react';
import {
    preloadChartData,
    preloadMapData,
    preloadHeatmapData,
} from './templateDataLoaders';
import ExportChartFooter from './components/ExportChartFooter';
import ExportChartLegends from './components/ExportChartLegends';
import ExportChartMainView from './components/ExportChartMainView';
import ExportChartHeader from './components/ExportChartHeader';

type Props = {
    chartConfig: IExportChartConfig;
};

const CHART_VIEWS = [
    VIEW.COUNTRY_GRAPH,
    VIEW.COUNTRY_GRAPH_SELECT_COUNTRY,
    VIEW.WORLD_GRAPH,
    VIEW.CONTINGENT_GRAPH,
];

const MAP_VIEWS = [
    VIEW.RISK_VIEW,
    VIEW.SORT_BY_RISK,
    VIEW.SORT_BY_DELTA,
    VIEW.CONTINGENT,
    VIEW.SORT_BY_CONTINGENT_RISK,
    VIEW.SORT_BY_CONTINGENT_DELTA,
];

const ExportChartTemplate = (props: Props) => {
    const [chartData, setChartData] = useState<any>(null);
    const chartConfig = props.chartConfig;

    useEffect(() => {
        const init = async () => {
            if (!chartConfig) return;

            initRootStore();

            const {
                chartStore,
                risksStore,
                countryStore,
                heatmapStore,
            } = rootStore;

            const { baseDate, virtualToday } = chartConfig;

            const rangeLeft = moment(chartConfig.baseDate).diff(
                moment(chartConfig.startDate),
                'days'
            );
            const rangeRight = moment(chartConfig.endDate).diff(
                moment(chartConfig.baseDate),
                'days'
            );

            chartStore.baseDate = moment(baseDate);
            chartStore.virtualToday = moment(virtualToday);
            chartStore.rangeLeft = rangeLeft;
            chartStore.rangeRight = rangeRight;
            chartStore.showCircles = false;
            chartStore.showLines = true;
            
            if (chartConfig.showVirtualToday !== undefined) {
                chartStore.showVirtualToday = chartConfig.showVirtualToday;
            }

            if (chartConfig.frequency) {
                chartStore.compositeDataInterval = chartConfig.frequency;
            }

            const dimensionList = chartConfig.displayNames.dimensions;
            risksStore.updateDimensions({
                allDimensions: dimensionList,
                flatten: Object.values(dimensionList),
                hashed: keyBy((dim: IActiveDimension) => dim.id, dimensionList),
            } as any);

            risksStore.updateClientFacingIndicators(
                chartConfig.displayNames.clientFacingIndicators
            );
            risksStore.updateContingentRisks(
                Object.values(chartConfig.displayNames.contingentRisks)
            );

            if (chartConfig.currencyOn) {
                risksStore.setCurrencyCountries(chartConfig.currencyCountry);
            }

            if (chartConfig.viewType === VIEW.HEATMAP) {
                const heatmap: IHeatmapStore = chartConfig.heatmap;
                heatmapStore.setRiskType(chartConfig.riskType);
                heatmapStore.setStartDate(moment(heatmap.startDate).toDate());
                heatmapStore.setTimeSpan(heatmap.timeSpan);
                heatmapStore.setScoreType(heatmap.scoreType);
                heatmapStore.setCountriesList(chartConfig.countries);
                heatmapStore.setSelectedCountry(heatmap.selectedCountry);
                heatmapStore.setSelectedRisk(heatmap.selectedRisk);
                heatmapStore.setSelectedPercentile(heatmap.selectedPercentile);
            }

            risksStore.initRisksByType();
            risksStore.setCurrentRiskType(chartConfig.riskType);
            risksStore.currentStore.overrideValues(...chartConfig.riskIds);

            risksStore.setStacks(
                ColorStack.fromSerialize(chartConfig.colorsStack.dimensions),
                ColorStack.fromSerialize(
                    chartConfig.colorsStack.clientFacingIndicators
                )
            );

            countryStore.setStacks(
                ColorStack.fromSerialize(chartConfig.colorsStack.countries)
            );
            countryStore.currentStore.overrideValues(...chartConfig.countries);

            const countryDataList = chartConfig.displayNames.countries;
            countryStore.updateCountries({
                countries: countryDataList,
                all: countryDataList,
                licensed: countryDataList,
            });

            risksStore.projectionDataOn = chartConfig.projectionDataOn;
            risksStore.riskChartMode = chartConfig.riskChartMode;
            risksStore.overrideGlobalIndicators(
                chartConfig.globalIndexTypes || []
            );

            if (chartConfig.macroeconomicsCountry?.length) {
                risksStore.setMacroeconomicsCountry(
                    chartConfig.macroeconomicsCountry[0],
                    chartConfig.globalIndexTypes?.[0]
                );
            }

            if (chartConfig.hasActiveCustomWeight) {
                rootStore.customWeightStore.updateActivePreset({
                    status: E_CW_STATUS.READY,
                    activePreset: '',
                    activePresetId: null,
                });
            }

            let preloadedData = null;

            if (CHART_VIEWS.includes(chartConfig.viewType)) {
                preloadedData = await preloadChartData(chartConfig);
            } else if (chartConfig.viewType === VIEW.HEATMAP) {
                preloadedData = await preloadHeatmapData(chartConfig);
            } else if (MAP_VIEWS.includes(chartConfig.viewType)) {
                preloadedData = await preloadMapData(chartConfig);
            }

            setChartData(preloadedData);
        };

        init();
    }, [chartConfig]);

    if (!chartData) {
        return (
            <div
                style={{
                    display: 'flex',
                    height: '100vh',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                <GQLoader
                    style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                />
            </div>
        );
    }

    return (
        <div className={cx(['gq-template-component-v2', { heatmap: chartConfig.viewType === VIEW.HEATMAP }])}>
            <ExportChartHeader chartConfig={chartConfig} />
            <ExportChartMainView chartConfig={chartConfig} chartData={chartData} />
            <ExportChartLegends chartConfig={chartConfig} />
            <div style={{ height: '5px', width: '100%' }}></div>
             <div className="gq-template-component-footer-v2">
                <ExportChartFooter chartConfig={chartConfig} chartData={chartData} />
            </div>
        </div>
    );
};

export default ExportChartTemplate;
